import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const STORAGE_KEY = "hrms_company_filter";

export function getStoredCompanyId() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "all";
  } catch {
    return "all";
  }
}

export function setStoredCompanyId(id) {
  try {
    localStorage.setItem(STORAGE_KEY, id || "all");
  } catch {
    /* ignore */
  }
}

/** Shared company filter — persists selection across modules. */
export function useCompanyFilter() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyIdState] = useState(getStoredCompanyId);

  useEffect(() => {
    api.get("/companies")
      .then((r) => setCompanies(r.data || []))
      .catch(() => setCompanies([]));
  }, []);

  const setCompanyId = (id) => {
    const next = id || "all";
    setCompanyIdState(next);
    setStoredCompanyId(next);
  };

  const companyOptions = useMemo(
    () => [
      { value: "all", label: "All companies" },
      ...companies.map((c) => ({
        value: c.id,
        label: c.name || c.legal_name || c.id,
      })),
    ],
    [companies]
  );

  /** Drop into FilterBar `filters` array */
  const companyFilter = useMemo(
    () => ({
      type: "select",
      key: "company",
      label: "Company",
      value: companyId,
      onChange: setCompanyId,
      options: companyOptions,
    }),
    [companyId, companyOptions]
  );

  /** Params for GET /employees */
  const empParams = useMemo(() => {
    if (!companyId || companyId === "all") return {};
    return { company_id: companyId };
  }, [companyId]);

  const filterEmployees = (emps) => {
    if (!companyId || companyId === "all") return emps || [];
    return (emps || []).filter((e) => e.company_id === companyId);
  };

  const companyLabel = useMemo(() => {
    if (!companyId || companyId === "all") return "All companies";
    const c = companies.find((x) => x.id === companyId);
    return c?.name || c?.legal_name || companyId;
  }, [companies, companyId]);

  return {
    companies,
    companyId,
    setCompanyId,
    companyFilter,
    companyOptions,
    empParams,
    filterEmployees,
    companyLabel,
  };
}
